import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { exchangeSessionId } from "../lib/commenter";
import { useCommenter } from "../context/CommenterContext";
import { api, formatApiErrorDetail } from "../lib/api";

/**
 * Handles the `#session_id=...` fragment dropped by Emergent's Google
 * auth portal. We exchange the one-shot id for a real cookie, strip the
 * fragment off the URL, and route the visitor back to whatever page
 * they were trying to comment on.
 *
 * Per the playbook, this MUST run synchronously on first render and
 * use a useRef guard (not useState) so React Strict Mode's double-
 * mount doesn't burn the single-use session_id.
 *
 * The Forum uses the same OAuth portal but exchanges the session via a
 * separate endpoint (`/forum/auth/google`) and stores its own cookie
 * (`forum_session`). It signals that flow via `?ctx=forum` on the
 * redirect URL.
 *
 * This component — not Forum.jsx — is what actually runs on return from
 * Google: App.js's <AuthRouter> intercepts any route whose hash contains
 * `session_id=` and renders this instead of the target page, so it's the
 * only place that can see the one-shot session_id. A signed-in forum
 * member who clicked "Link Google account" (instead of plain sign-in)
 * sets a `forum_link_intent` sessionStorage flag right before the
 * redirect — if that flag is present, exchange via `/forum/auth/link/
 * google` (merges into their current account) instead of `/forum/auth/
 * google` (which would just sign them into whatever separate account
 * that Google identity resolves to).
 */
export default function AuthCallback() {
  const { refresh } = useCommenter();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : "";

    const params = new URLSearchParams(window.location.search);
    const ctx = params.get("ctx") || "commenter";
    const currentPath = window.location.pathname || "/";
    const defaultNext = ctx === "forum" ? "/thelastpost" : currentPath;
    const next = params.get("next") || defaultNext;
    const isForumLink = ctx === "forum" && sessionStorage.getItem("forum_link_intent") === "1";
    sessionStorage.removeItem("forum_link_intent");

    const finish = async () => {
      if (sessionId) {
        try {
          if (ctx === "forum") {
            await api.post(isForumLink ? "/forum/auth/link/google" : "/forum/auth/google", { session_id: sessionId });
          } else {
            await exchangeSessionId(sessionId);
            await refresh();
          }
        } catch (e) {
          // Surface link failures (e.g. the 409 "already belongs to
          // another member" conflict) back on the forum page instead of
          // silently dropping them — plain sign-in failures still fall
          // through quietly (the SignInPanel reappearing is feedback
          // enough there).
          if (isForumLink) {
            sessionStorage.setItem(
              "forum_link_error",
              formatApiErrorDetail(e?.response?.data?.detail) || "Could not link that account. Try again.",
            );
          }
        }
      }
      navigate(next, { replace: true });
    };

    finish();
  }, [navigate, refresh]);

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-[var(--page-bg)]"
      data-testid="auth-callback"
    >
      <div className="flex items-center gap-3 text-sm text-[var(--brand-body)]">
        <Loader2 size={16} className="animate-spin" />
        Signing you in…
      </div>
    </div>
  );
}
